/**
 * Product page behaviour: the pack picker, the gallery with its zoom dialog,
 * and the sticky add-to-cart. Each is a custom element, so the theme editor's
 * section re-renders tear them down and wire them up again on their own.
 */
import {lockPageScroll} from '@theme/theme';

const money = new Intl.NumberFormat('tr-TR', {style: 'currency', currency: 'TRY'});
const formatCents = (cents) => money.format(Number(cents) / 100);

/* ------------------------------------------------------------- purchase
   The pack picker. Choosing a pack writes its quantity into the product
   form's own `quantity` field (which the add-to-cart and EasySell's
   cash-on-delivery form both read) and shows that pack's total everywhere a
   price is printed: above the picker, on the button and in the sticky bar. */
class ZivaPdpPurchase extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.form = document.getElementById(this.dataset.formId);
    this.addEventListener('change', (event) => {
      if (event.target.name === 'quantity-break') this.select(event.target);
    }, {signal: this.abort.signal});

    const checked = this.querySelector('input[name="quantity-break"]:checked');
    if (checked) this.select(checked);
    if (this.hasAttribute('data-check-packs')) this.checkPacks();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  select(input) {
    const quantity = Number(input.value);
    const {total, reference, savings} = input.dataset;

    this.querySelectorAll('.ziva-pdp-bundle').forEach((label) => {
      label.classList.toggle('is-selected', label.contains(input));
    });
    this.form?.querySelectorAll('[data-pack-quantity], input[name="quantity"]').forEach((field) => {
      field.value = String(quantity);
    });

    const hasSavings = Number(savings) > 0;
    document.querySelectorAll('[data-pack-price]').forEach((block) => {
      const strong = block.querySelector('strong');
      if (strong) strong.textContent = formatCents(total);
      let struck = block.querySelector('s');
      let gain = block.querySelector('small');
      if (hasSavings) {
        if (!struck) {
          struck = document.createElement('s');
          strong?.after(struck);
        }
        const hidden = struck.querySelector('.visually-hidden');
        struck.textContent = formatCents(reference);
        if (hidden) struck.prepend(hidden);
        if (gain || block.classList.contains('ziva-pdp-bundle-price')) {
          if (!gain) {
            gain = document.createElement('small');
            block.append(gain);
          }
          gain.textContent = `${formatCents(savings)} kazanç`;
        }
      } else {
        struck?.remove();
        gain?.remove();
      }
    });
    document.querySelectorAll('[data-pack-button-price]').forEach((el) => {
      el.textContent = formatCents(total);
    });
    document.querySelectorAll('[data-pack-count]').forEach((el) => {
      el.textContent = String(quantity);
    });
  }

  /* Theme editor only. The pack totals are typed into theme settings because
     Liquid cannot read Shopify's automatic discounts; here, where no shopper
     or pixel is watching, they are checked against what Shopify's own cart
     charges, and a mismatch is flagged in red. The estimate carts are never
     attached to anyone. */
  async checkPacks() {
    const warning = this.querySelector('[data-pack-warning]');
    const variant = `gid://shopify/ProductVariant/${this.dataset.variantId}`;
    const query = `mutation($lines:[CartLineInput!]!){cartCreate(input:{lines:$lines}){cart{cost{totalAmount{amount}}}}}`;
    const problems = [];

    for (const input of this.querySelectorAll('input[name="quantity-break"]')) {
      const quantity = Number(input.value);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}api/2025-07/graphql.json`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({query, variables: {lines: [{merchandiseId: variant, quantity}]}}),
        });
        const data = await response.json();
        const amount = data?.data?.cartCreate?.cart?.cost?.totalAmount?.amount;
        if (amount === undefined) continue;
        const shopify = Math.round(Number(amount) * 100);
        if (shopify !== Number(input.dataset.total)) {
          problems.push(`${quantity} adet: sayfada ${formatCents(input.dataset.total)}, Shopify'da ${formatCents(shopify)}`);
        }
      } catch {
        // The check is a convenience for the editor; a failed request says nothing.
      }
    }

    if (warning && problems.length) {
      warning.hidden = false;
      warning.textContent = `Paket fiyatları Shopify ile uyuşmuyor (Tema ayarları › Paket fiyatları): ${problems.join(' · ')}`;
    }
  }
}

customElements.define('ziva-pdp-purchase', ZivaPdpPurchase);

/* -------------------------------------------------------------- gallery
   One photograph at a time with a counter and thumbnails; on a phone the
   slides are a swipeable row. A press on a photograph opens every medium in
   a scrolling dialog at the one pressed. */
class ZivaPdpGallery extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const {signal} = this.abort;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('.ziva-pdp-gallery__slide')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.dialog = this.querySelector('.ziva-pdp-media-modal');
    this.mobile = window.matchMedia('(max-width: 900px)');
    this.active = 0;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.show(this.active - 1), {signal});
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.show(this.active + 1), {signal});
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.show(Number(thumb.dataset.galleryThumb), 'auto'), {signal});
    });
    this.querySelectorAll('[data-gallery-zoom]').forEach((button) => {
      button.addEventListener('click', () => this.openModal(Number(button.dataset.galleryZoom), button), {signal});
    });

    this.track?.addEventListener('scroll', () => {
      if (!this.mobile.matches) return;
      const next = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
      if (next !== this.active && next >= 0 && next < this.slides.length) this.setActive(next);
    }, {signal, passive: true});

    if (this.dialog) {
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeModal();
      }, {signal});
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeModal();
      }, {signal});
      this.dialog.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.closeModal(), {signal});
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.unlock?.();
  }

  setActive(index) {
    this.active = index;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      if (i !== index) slide.querySelector('video')?.pause();
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const counter = this.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = String(index + 1);
  }

  show(index, behavior = 'smooth') {
    const count = this.slides.length;
    if (!count) return;
    const next = (index + count) % count;
    this.setActive(next);
    if (this.mobile.matches && this.track) {
      this.track.scrollTo({
        left: this.track.clientWidth * next,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior,
      });
    }
  }

  openModal(index, opener) {
    if (!this.dialog) return;
    this.setActive(index);
    this.opener = opener;
    this.dialog.querySelectorAll('.ziva-pdp-media-modal__item').forEach((item, i) => {
      item.classList.toggle('active', i === index);
    });
    this.unlock = lockPageScroll();
    this.dialog.showModal();
    this.dialog.querySelector(`[data-modal-media-index="${index}"]`)?.scrollIntoView({block: 'start'});
    this.dialog.querySelector('[data-gallery-close]')?.focus();
  }

  closeModal() {
    if (!this.dialog?.open) return;
    this.dialog.close();
    this.unlock?.();
    this.unlock = undefined;
    this.opener?.focus();
  }
}

customElements.define('ziva-pdp-gallery', ZivaPdpGallery);

/* --------------------------------------------------------------- sticky
   Shown from when the product form (the one its button submits) has left the
   top of the screen until the page's content ends, so it never sits over the
   footer, and never while a drawer is open. Hidden, it is inert, so its
   button takes no focus. */
class ZivaPdpSticky extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const {signal} = this.abort;
    this.form = this.querySelector('button[form]')?.form;
    this.main = document.getElementById('MainContent');
    let frame = 0;
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          this.measure();
        });
      }
    };
    window.addEventListener('scroll', schedule, {signal, passive: true});
    window.addEventListener('resize', schedule, {signal});
    document.addEventListener('drawer:open', () => this.update(false), {signal});
    document.addEventListener('drawer:close', schedule, {signal});
    this.measure();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  measure() {
    if (!this.form || !this.main) return;
    const inReach =
      this.form.getBoundingClientRect().bottom < 0 &&
      this.main.getBoundingClientRect().bottom > window.innerHeight;
    const drawerOpen = document.documentElement.classList.contains('is-scroll-locked');
    this.update(inReach && !drawerOpen);
  }

  update(visible) {
    this.dataset.visible = String(visible);
    this.inert = !visible;
  }
}

customElements.define('ziva-pdp-sticky', ZivaPdpSticky);
